/**
 * "Agendar consulta" no painel do Atendimento (fase 2 da agenda da clínica).
 *
 * O paciente pede pela conversa; a automação cria a oportunidade; daqui a
 * recepção agenda sem sair da conversa. A origem do agendamento vem marcada
 * pelo canal (WhatsApp, quando a conversa é de lá).
 *
 * ── O painel vira o agendamento (25/10) ──────────────────────────────────
 *
 * O botão não abre diálogo: o painel do cliente inteiro passa a ser o
 * agendamento, e a coluna dobra de largura para a esquerda, espremendo a
 * conversa (a tela cuida da largura, por `data-agendando`). As etapas vêm uma
 * por vez — oportunidade, médico e especialidade; dia e horário; dados —, para
 * ninguém rolar atrás do próximo campo. Fechar devolve o painel do cliente.
 *
 * A consulta mora na oportunidade: a etapa 1 começa pela escolha dela, com
 * busca. Sem nenhuma, o bloco diz onde ela nasce, em vez de um botão sem onde
 * gravar.
 *
 * Com mais de uma, nenhuma vem escolhida: o paciente pode ter um card no
 * agendamento e outro em outro funil (o dos exames, que uma rotina move toda
 * hora e por isso fica no topo da lista). Pré-escolher a primeira gravava a
 * consulta no card errado. Cada opção diz o funil e a etapa.
 */

import { useState, type ReactNode } from 'react';
import { AbaDaConsulta } from '@/widgets/detalhe-da-oportunidade';
import type { ContatoDoPaciente } from '@/features/saude/vincular-paciente';
import { Botao, CampoEscolha, Icone } from '@/shared/ui';

export type OportunidadeParaAgendar = {
  readonly id: string;
  readonly titulo: string | null;
  /** A etapa como se lê, com o funil: "Paciente agendado · Agendamento". */
  readonly etapa?: string | null;
};

/** O nome da opção: o título e onde o card está. */
function rotuloDaOportunidade(o: OportunidadeParaAgendar): string {
  const titulo = o.titulo ?? 'Oportunidade sem título';
  return o.etapa ? `${titulo} · ${o.etapa}` : titulo;
}

/** O bloco "Consulta" do painel: o botão que abre o agendamento, e o aviso da volta. */
export function AgendarConsulta({
  oportunidades,
  aviso,
  aoAbrir,
}: {
  oportunidades: readonly OportunidadeParaAgendar[];
  /** "Consulta agendada no SISAC.", depois de agendar. */
  aviso: string | null;
  aoAbrir: () => void;
}): ReactNode {
  if (oportunidades.length === 0) {
    return (
      <p className="contexto__nota">
        Para agendar, o contato precisa de uma oportunidade em aberto. Crie uma em "Criar
        oportunidade".
      </p>
    );
  }
  return (
    <>
      {aviso && (
        <p className="contexto__nota" role="status">
          {aviso}
        </p>
      )}
      <Botao variante="primario" onClick={aoAbrir}>
        Agendar consulta
      </Botao>
    </>
  );
}

/** O painel inteiro no agendamento: o cabeçalho com "Fechar" e as etapas. */
export function AgendamentoNoPainel({
  oportunidades,
  contato,
  canal,
  aoAgendar,
  aoFechar,
}: {
  oportunidades: readonly OportunidadeParaAgendar[];
  contato: ContatoDoPaciente;
  /** O canal da conversa: diz a origem que vem marcada. */
  canal: string | null;
  aoAgendar: () => void;
  aoFechar: () => void;
}): ReactNode {
  // Uma só vem escolhida; com mais de uma, a recepção escolhe.
  const [escolhida, definirEscolhida] = useState(
    oportunidades.length === 1 ? oportunidades[0]!.id : '',
  );
  // A lista muda com o tempo real (um card ganho sai dela): sobrando uma só, é ela.
  const atual =
    oportunidades.find((o) => o.id === escolhida) ??
    (oportunidades.length === 1 ? oportunidades[0] : undefined);
  const escolha = (
    <CampoEscolha
      id="agendamento-oportunidade"
      rotulo="Oportunidade"
      buscavel
      valor={atual?.id ?? ''}
      vazio="Escolha a oportunidade"
      opcoes={oportunidades.map((o) => ({ valor: o.id, rotulo: rotuloDaOportunidade(o) }))}
      aoEscolher={definirEscolhida}
    />
  );
  const origem = (canal ?? '').toLowerCase().includes('whatsapp') ? 'WhatsApp' : 'Atendimento';

  return (
    <aside className="col col--painel agendamento" aria-labelledby="agendamento-titulo">
      <header className="agendamento__topo">
        <span className="agendamento__icone" aria-hidden="true">
          <Icone nome="calendario" />
        </span>
        <div className="agendamento__quem">
          <h2 className="agendamento__titulo" id="agendamento-titulo">
            Agendar consulta
          </h2>
          {contato.nome && <span className="agendamento__contato">{contato.nome}</span>}
        </div>
        <button
          type="button"
          className="painel-alternar"
          aria-label="Fechar o agendamento"
          title="Fechar o agendamento"
          onClick={aoFechar}
        >
          <Icone nome="fechar" />
        </button>
      </header>
      <div className="col__rolagem agendamento__corpo">
        {atual ? (
          /* A chave recomeça o agendamento ao trocar de oportunidade: nada da outra fica. */
          <AbaDaConsulta
            key={atual.id}
            oportunidadeId={atual.id}
            podeEditar
            contato={contato}
            origemPadrao={origem}
            comecarAgendando
            forma="passos"
            noComeco={escolha}
            aoAgendar={aoAgendar}
            aoCancelarAgendamento={aoFechar}
          />
        ) : oportunidades.length > 1 ? (
          <>
            <p className="contexto__nota">
              O contato tem {oportunidades.length} oportunidades em aberto. Escolha em qual a
              consulta fica.
            </p>
            {escolha}
          </>
        ) : (
          <p className="contexto__nota">
            Para agendar, o contato precisa de uma oportunidade em aberto. Crie uma em "Criar
            oportunidade".
          </p>
        )}
      </div>
    </aside>
  );
}
